import { useState, useRef, useEffect } from "react";
import { X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { MessageReactions } from "./MessageReactions";
import { MessageHoverActions } from "./MessageHoverActions";
import { cn } from "@/lib/utils";

interface ThreadMessage {
  id: string;
  message: string;
  sender_id: string;
  created_at: string;
  sender?: {
    id: string;
    full_name: string | null;
  };
}

interface ThreadPanelProps {
  parentMessage: ThreadMessage;
  replies: ThreadMessage[];
  currentUserId: string;
  messageType: 'channel' | 'direct';
  onClose: () => void;
  onSendReply: (message: string) => Promise<void>;
  isLoading?: boolean;
  isSending?: boolean;
}

export const ThreadPanel = ({
  parentMessage,
  replies,
  currentUserId,
  messageType,
  onClose,
  onSendReply,
  isLoading,
  isSending,
}: ThreadPanelProps) => {
  const [replyText, setReplyText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const LONG_MESSAGE_THRESHOLD = 300;
  const isLongMessage = (text: string) => text.length > LONG_MESSAGE_THRESHOLD;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [replies]);

  const handleSend = async () => {
    if (!replyText.trim() || isSending) return;
    await onSendReply(replyText.trim());
    setReplyText("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderMessage = (msg: ThreadMessage, isParent = false) => {
    const isOwn = msg.sender_id === currentUserId;
    const senderName = msg.sender?.full_name || 'Unknown';

    return (
      <div
        key={msg.id}
        className={cn(
          "group relative p-3 rounded-lg",
          isParent ? "bg-muted/50 border border-border mb-4" : "hover:bg-muted/30"
        )}
      >
        <div className="flex items-start gap-3">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className={cn(
              "text-xs",
              isOwn ? "bg-primary text-primary-foreground" : "bg-muted"
            )}>
              {getInitials(senderName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{senderName}</span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
              </span>
            </div>
            <div 
              className="text-sm mt-0.5 whitespace-pre-wrap break-words max-h-[150px] overflow-y-auto scrollable-content pr-1"
              style={{ touchAction: 'pan-y' }}
            >{msg.message}</div>
            <MessageReactions
              messageId={msg.id}
              messageType={messageType}
              currentUserId={currentUserId}
            />
          </div>
        </div>
        {!isParent && (
          <MessageHoverActions
            onReply={() => {}}
            onAddReaction={() => {}}
            showReply={false}
            className="opacity-0 group-hover:opacity-100"
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full border-l border-border bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-semibold">Thread</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {renderMessage(parentMessage, true)}

        {isLoading ? (
          <div className="text-center text-muted-foreground text-sm py-4">
            Loading replies...
          </div>
        ) : replies.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-4">
            No replies yet. Start the conversation!
          </div>
        ) : (
          <div className="space-y-1">
            {replies.map(reply => renderMessage(reply))}
          </div>
        )}
      </ScrollArea>

      {/* Reply Input */}
      <div className="p-4 border-t border-border">
        <div className="flex items-end gap-2">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply in thread..."
            className="min-h-[60px] max-h-[180px] resize-none"
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!replyText.trim() || isSending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
